import { action, makeObservable, observable, runInAction, IObservableArray } from 'mobx';
import { Injectable } from './service.base';
import { AxiosResponse } from 'axios';
import { parsePhoneNumber } from 'libphonenumber-js/max';
import { api } from '~/tools/api/api';
import { plainToInstance } from 'class-transformer';
import moment from 'moment';
import { EApiUrl } from '~/tools/api/enums';
import { ICreateAccount, ILinkAccount, IOtpData } from '~/views/types';
import { ModelCountry } from '~/models/model.country';
import { ModelUnityDocuments } from '~/models/model.unity.documents';
import { WrapperService } from '~/services/service.wrapper';
import { useService } from '~/services/service.base';
import { extractServerError } from '~/tools/error';

@Injectable()
export class LinkAccountDialogService {
    @observable public open = false;
    @observable public showSignUp = false;
    @observable public showStep1 = true;
    @observable public showStep3 = false;
    @observable public showNotSuccess = false;
    @observable public showLinkAccount = false;
    @observable public showLoading = false;

    @observable public selectedCountry = '0';
    @observable public isPhoneInvalid = false;
    @observable public isEmailInvalid = false;
    @observable public isLinkUnityEmailInvalid = false;

    @observable public isDateOfBirthInvalid = false;
    @observable public isFirstNameInvalid = false;
    @observable public isLastNameInvalid = false;
    @observable public isDateOfBirthLinkInvalid = false;
    @observable public isCreateAccountBtnDisabled = true;
    @observable public isLinkAccountBtnDisabled = true;
    @observable public isLinkUnityAuthError = false;
    @observable public isAgreeOneChecked = false;
    @observable public isAgreeTwoChecked = false;
    @observable public showActivate = false;
    @observable public lastError: { description: string; code?: string } = { description: '', code: undefined };

    public account: ICreateAccount = {};
    public linkAccount: ILinkAccount = {};
    public countries: IObservableArray<ModelCountry> = observable.array([]);
    private otpData: IOtpData;
    private authService: any;
    public unityDocuments: ModelUnityDocuments;
    private phoneNumber: { nationalNumber: string; countryCallingCode: string } | null = null;

    constructor() {
        const wrapperService = useService(WrapperService);
        const { authService } = wrapperService;
        this.authService = authService;
        makeObservable(this);
        const storage: any = window.sessionStorage.getItem('linkAccountDialog');
        if (storage) {
            this.openFromStorage(storage);
        }
    }

    @action
    handleOpen = () => {
        this.open = true;
        this.addToSessionStorage({ step: 1 });
    };

    @action
    handleClose = () => {
        this.open = false;
        this.setShowStep1(true);
        this.setShowSignUp(false);
        this.setShowLinkAccount(false);
        this.setShowNotSuccess(false);
        this.setShowActivate(false);
        this.setShowStep3(false);
        this.isCreateAccountBtnDisabled = true;

        this.selectedCountry = '0';
        this.removeFromSessionStorage();
        this.clearAllErrors();
        this.account = {};
        this.linkAccount = {};
    };

    @action
    handleOpenSignUp = async () => {
        await this.getCountries();
        await this.getDocuments();
        this.setShowStep1(false);
        this.setShowSignUp(true);
        this.addToSessionStorage({ step: 2 });
    };

    @action
    handleOpenLinkAccount = () => {
        this.setShowStep1(false);
        this.setShowLinkAccount(true);
        this.addToSessionStorage({ step: 3 });
    };

    @action
    setShowStep1 = (value: boolean) => {
        this.showStep1 = value;
    };

    @action
    setShowSignUp = (value: boolean) => {
        this.showSignUp = value;
    };

    @action
    setShowLinkAccount = (value: boolean) => {
        this.showLinkAccount = value;
    };

    @action
    handleCountryChange = (event: { target: { value: string } }) => {
        this.selectedCountry = event.target.value;

        if (this.selectedCountry === '0') {
            this.account.country = null;
        } else {
            this.account.country = this.selectedCountry;
        }
        this.validateAllFields();
    };

    @action
    handlePhoneNumberBlur = (event: { target: { value: string } }) => {
        const originPhone = event.target.value.trim().replace(/\s/g, '');

        if (originPhone) {
            this.phoneNumber = null;
            try {
                const phoneNumber = parsePhoneNumber(originPhone, originPhone.startsWith('+') ? undefined : 'US');
                const phoneType = phoneNumber?.getType();

                if (
                    phoneNumber &&
                    phoneNumber.isValid() &&
                    phoneType &&
                    ['MOBILE', 'FIXED_LINE_OR_MOBILE'].includes(phoneType)
                ) {
                    this.phoneNumber = phoneNumber;
                }
            } catch (error: any) {
                this.isPhoneInvalid = true;
            }

            if (!this.isPhoneInvalid && !this.phoneNumber) {
                this.isPhoneInvalid = true;
            }
        }

        this.validateAllFields();
    };

    @action
    handlePhoneNumberFocus = () => {
        this.isPhoneInvalid = false;
    };

    @action
    handleEmailBlur = (event: { target: { value: string } }) => {
        if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(event.target.value)) {
            this.isEmailInvalid = true;
            this.account.email = null;
        } else {
            this.isEmailInvalid = false;
            this.account.email = event.target.value;
        }
        this.validateAllFields();
    };

    @action
    handleEmailFocus = () => {
        this.isEmailInvalid = false;
    };

    @action
    handleFirstNameBlur = (event: { target: { value: string } }) => {
        if (!event.target.value) {
            this.account.firstName = null;
            return;
        }

        if (!/^[a-zA-Z\s]+$/i.test(event.target.value)) {
            this.isFirstNameInvalid = true;
            this.account.firstName = null;
        } else {
            this.isFirstNameInvalid = false;
            this.account.firstName = event.target.value;
        }
        this.validateAllFields();
    };

    @action
    handleFirstNameFocus = () => {
        this.isFirstNameInvalid = false;
    };

    @action
    handleLastNameBlur = (event: { target: { value: string } }) => {
        if (!event.target.value) {
            this.account.lastName = null;
            return;
        }

        if (!/^[a-zA-Z\s]+$/i.test(event.target.value)) {
            this.isLastNameInvalid = true;
            this.account.lastName = null;
        } else {
            this.isLastNameInvalid = false;
            this.account.lastName = event.target.value;
        }
        this.validateAllFields();
    };

    @action
    handleLastNameFocus = () => {
        this.isLastNameInvalid = false;
    };

    @action
    handleDateOfBirthBlur = (event: { target: { value: string } }) => {
        if (event.target.value) {
            const dateArr: Array<string> = event.target.value.split('/');
            if (dateArr.length < 3) {
                this.isDateOfBirthInvalid = true;
            } else {
                const month: number = dateArr[0] as unknown as number;
                const day: number = dateArr[1] as unknown as number;
                const year: number = dateArr[2] as unknown as number;

                const dateFormat = 'YYYY-MM-DD';
                const date = `${year}-${month}-${day}`;
                const toDateFormat = moment(new Date(date)).format(dateFormat);

                if (moment(toDateFormat, dateFormat, true).isValid()) {
                    const currentYear: number = new Date().getFullYear();
                    const allowYear: number = currentYear - 18;
                    const hunderYearAgo: number = currentYear - 100;

                    if (year > allowYear || year < hunderYearAgo) {
                        this.isDateOfBirthInvalid = true;
                    } else {
                        this.account.dateBirth = event.target.value;
                        this.isDateOfBirthInvalid = false;
                    }
                } else {
                    this.isDateOfBirthInvalid = true;
                }
            }
        } else {
            this.account.dateBirth = null;
        }
        this.validateAllFields();
    };

    @action
    handleDateOfBirthFocus = () => {
        this.isDateOfBirthInvalid = false;
    };

    @action
    validateAllFields = () => {
        if (
            this.account.firstName &&
            this.account.lastName &&
            this.account.dateBirth &&
            this.account.country &&
            this.account.email &&
            !this.isPhoneInvalid &&
            this.isAgreeTwoChecked
        ) {
            this.isCreateAccountBtnDisabled = false;
        } else {
            this.isCreateAccountBtnDisabled = true;
        }
        this.addToSessionStorage({ step: 2, data: { account: this.account } });
    };

    @action
    getCountries = async () => {
        try {
            /*const response: AxiosResponse = await api.get<ModelCountry[]>(EApiUrl.countries);
        if (response.statusText?.toLowerCase() === 'ok') {
          const countries = plainToInstance(ModelCountry, response.data);
          this.setCountries(countries);
        }*/
            const data = [
                {
                    id: '1',
                    name: 'Canada',
                },
                {
                    id: '2',
                    name: 'USA',
                },
            ];
            const countries = plainToInstance(ModelCountry, data);
            this.setCountries(countries);
        } catch (error: any) {}
    };

    @action
    setCountries = (countries: any) => {
        this.countries.replace(countries);
    };

    @action
    verifyOtpUnityAccount = async (verificationCode: string): Promise<boolean> => {
        this.clearLastError();
        let verifyResult = false;

        this.otpData.otp = verificationCode;
        try {
            const response: AxiosResponse = await api.post(
                EApiUrl.unityOtp,
                this.otpData,
                {
                    headers: {
                        Authorization: this.authService.currentUser?.token,
                    },
                },
                true,
            );

            if (response.status === 200 && !response.data.error) {
                verifyResult = true;
                this.authService.getUnityUser();
            }
        } catch (error: any) {
            this.setLastError(extractServerError(error?.response?.data ?? error));
        }

        return verifyResult;
    };

    @action
    setShowLoading = (value: boolean) => {
        this.showLoading = value;
    };

    @action
    setShowNotSuccess = (value: boolean) => {
        this.showNotSuccess = value;
    };

    @action
    handleUnityEmailBlur = (event: { target: { value: string } }) => {
        this.setLinkUnityAuthError(false);
        if (event.target.value) {
            if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(event.target.value)) {
                this.isLinkUnityEmailInvalid = true;
                this.linkAccount.unityAccountEmail = null;
            } else {
                this.isLinkUnityEmailInvalid = false;
                this.linkAccount.unityAccountEmail = event.target.value;
            }
        } else {
            this.linkAccount.unityAccountEmail = null;
        }
        this.validateLinkAccountAllFields();
    };

    @action
    validateLinkAccountAllFields = () => {
        this.isLinkAccountBtnDisabled = (!this.linkAccount.unityAccountEmail ||
            !this.linkAccount.dateBirth) as unknown as boolean;
        this.addToSessionStorage({ step: 3, data: { linkAccount: this.linkAccount } });
    };

    @action
    handleDateOfBirthLinkChange = (event: { target: { value: string } }) => {
        if (event.target.value) {
            const dateArr: Array<string> = event.target.value.split('/');
            if (dateArr.length < 3) {
                this.isDateOfBirthLinkInvalid = true;
            } else {
                const month: number = dateArr[0] as unknown as number;
                const day: number = dateArr[1] as unknown as number;
                const year: number = dateArr[2] as unknown as number;

                let isValid = true;

                if (month > 12) {
                    isValid = false;
                }

                if (isValid && day > 31) {
                    isValid = false;
                }

                const currentYear: number = new Date().getFullYear();
                const allowYear: number = currentYear - 18;

                if (isValid && (year > allowYear || year < 1000)) {
                    isValid = false;
                }

                this.isDateOfBirthLinkInvalid = !isValid;
                this.linkAccount.dateBirth = isValid ? event.target.value : null;
            }
        } else {
            this.linkAccount.dateBirth = null;
        }
        this.validateLinkAccountAllFields();
    };

    @action
    handleAgreeOneChange = () => {
        this.isAgreeOneChecked = !this.isAgreeOneChecked;
    };

    @action
    handleAgreeTwoChange = () => {
        this.isAgreeTwoChecked = !this.isAgreeTwoChecked;
        this.validateAllFields();
    };

    @action
    handleDateOfBirthLinkFocus = () => {
        this.isDateOfBirthLinkInvalid = false;
    };

    @action
    verifyOtpLinkUnity = async (verificationCode: string): Promise<boolean> => {
        this.clearLastError();
        let verifyResult = false;

        const data = {
            otp: verificationCode,
            transactionId: this.otpData.transactionId,
            unityUserId: this.otpData.unityUserId,
        };

        try {
            const response: AxiosResponse = await api.post(
                EApiUrl.unityOtp,
                data,
                {
                    headers: {
                        Authorization: this.authService.currentUser?.token,
                    },
                },
                true,
            );

            if (response.status === 200) {
                if (!response.data?.error) {
                    verifyResult = true;
                    this.authService.getUnityUser();
                }
            }
        } catch (error: any) {
            this.setLastError(extractServerError(error?.response?.data ?? error));
        }

        return verifyResult;
    };

    @action
    setShowStep3 = (value: boolean) => {
        this.showStep3 = value;
    };

    @action
    handleOpenStep3 = async () => {
        if (!this.isLinkAccountBtnDisabled) {
            const sendResult: boolean = await this.sendVerificationCode();

            if (sendResult) {
                this.setShowLinkAccount(false);
                this.setShowStep3(true);
            }
        }
    };

    @action
    afterEnterCode = async (verificationCode: string): Promise<boolean> => {
        let verifyResult = false;
        this.setShowLoading(true);

        if (this.linkAccount.unityAccountEmail) {
            verifyResult = await this.verifyOtpLinkUnity(verificationCode);
        } else {
            verifyResult = await this.verifyOtpUnityAccount(verificationCode);
        }

        this.setShowLoading(false);
        if (verifyResult) {
            this.handleClose();
        }

        return verifyResult;
    };

    sendVerificationCode = async (): Promise<boolean> => {
        this.clearLastError();
        let sendResult = false;

        this.setShowLoading(true);
        const pad = (n: number | string) => n.toString().padStart(2, '0');
        const email = this.linkAccount.unityAccountEmail ? this.linkAccount.unityAccountEmail : this.account.email;
        const dateStr = this.linkAccount.dateBirth ? this.linkAccount.dateBirth : this.account.dateBirth;
        let month: number | undefined;
        let day: number | undefined;
        let year: number | undefined;

        if (dateStr) {
            const dateArr = dateStr.split('/');
            month = Number(dateArr[0]);
            day = Number(dateArr[1]);
            year = Number(dateArr[2]);
        }

        const dateOfBirth = year && month && day ? `${year}-${pad(month)}-${pad(day)}` : '';
        const url = `${EApiUrl.unitySignIn}`;

        try {
            const response: AxiosResponse = await api.post(
                url,
                { email, dateOfBirth },
                {
                    headers: {
                        Authorization: this.authService.currentUser?.token,
                    },
                },
                true,
            );

            if (response.status === 200) {
                sendResult = true;
                this.otpData = response.data;
            }
        } catch (error: any) {
            this.setLastError(extractServerError(error?.response?.data ?? error));
            this.setLinkUnityAuthError(true);
        } finally {
            this.setShowLoading(false);
        }

        return sendResult;
    };

    @action
    setLinkUnityAuthError = (value: boolean) => {
        this.isLinkUnityAuthError = value;
    };

    @action
    getDocuments = async () => {
        try {
            const response: AxiosResponse = await api.get<ModelUnityDocuments>(
                `${EApiUrl.unityDocuments}?language=en`,
                {
                    headers: {
                        Authorization: this.authService.currentUser?.token,
                    },
                },
                true,
            );
            if (response.status === 200) {
                this.unityDocuments = plainToInstance(ModelUnityDocuments, response.data);
                for (const document of this.unityDocuments.documents) {
                    document.agreed = true;
                }
            }
        } catch (error: any) {}
    };

    @action
    unitySignUp = async (): Promise<void> => {
        this.clearLastError();
        this.setShowLoading(true);

        try {
            const dateArr: Array<string> = this.account.dateBirth ? this.account.dateBirth.split('/') : [];
            const month: number = dateArr[0] as unknown as number;
            const day: number = dateArr[1] as unknown as number;
            const year: number = dateArr[2] as unknown as number;

            const dateOfBirth = `${year}-${month}-${day}`;

            const data: {
                dateOfBirth: string;
                email: string | null | undefined;
                firstName: string | null | undefined;
                lastName: string | null | undefined;
                consents: any;
                mobile?: string;
                mobileCountryCode?: string;
            } = {
                dateOfBirth,
                email: this.account.email,
                firstName: this.account.firstName,
                lastName: this.account.lastName,
                consents: this.unityDocuments.documents,
            };

            if (this.phoneNumber) {
                data.mobile = (this.phoneNumber as { nationalNumber: string }).nationalNumber;
                data.mobileCountryCode = (this.phoneNumber as { countryCallingCode: string }).countryCallingCode;
            }

            const response: AxiosResponse = await api.post(
                EApiUrl.unitySignUp,
                data,
                {
                    headers: {
                        Authorization: this.authService.currentUser?.token,
                    },
                },
                true,
            );

            if (response.status === 200 && response.data.profileId) {
                await this.unitySignIn(dateOfBirth, this.account?.email);
            } else {
                this.setShowSignUp(false);
                this.setShowNotSuccess(true);
            }
        } catch (error: any) {
            this.setLastError(extractServerError(error?.response?.data ?? error));
            if (
                error.response &&
                error.response.status === 400 &&
                error.response.data &&
                error.response.data.code === 'GSN011'
            ) {
                this.setShowSignUp(false);
                this.setShowActivate(true);
            } else {
                this.setShowSignUp(false);
                this.setShowNotSuccess(true);
            }
        }

        this.setShowLoading(false);
    };

    @action
    setShowActivate = (value: boolean) => {
        this.showActivate = value;
    };

    @action
    unitySignIn = async (dateOfBirth: string, email: string | null | undefined): Promise<void> => {
        this.clearLastError();
        try {
            const response: AxiosResponse = await api.post(
                EApiUrl.unitySignIn,
                { dateOfBirth, email },
                {
                    headers: {
                        Authorization: this.authService.currentUser?.token,
                    },
                },
                true,
            );

            if (response.status === 200 && response.data.unityUserId) {
                this.otpData = response.data;
                this.setShowSignUp(false);
                this.setShowStep3(true);
            } else {
                this.setShowSignUp(false);
                this.setShowNotSuccess(true);
            }
        } catch (error: any) {
            this.setLastError(extractServerError(error?.response?.data ?? error));
            this.setShowSignUp(false);
            this.setShowNotSuccess(true);
        }
    };

    @action
    onStepKeyUp = (e: any) => {
        if (e.keyCode === 13 && this.showSignUp && !this.isCreateAccountBtnDisabled) {
            this.unitySignUp();
        } else if (e.keyCode === 13 && this.showLinkAccount && !this.isLinkAccountBtnDisabled) {
            this.handleOpenStep3();
        }
    };

    addToSessionStorage = (data: any) => {
        window.sessionStorage.setItem('linkAccountDialog', JSON.stringify(data));
    };

    openFromStorage = async (storage: string) => {
        const storageData = JSON.parse(storage);

        if (storageData.step) {
            switch (storageData.step) {
                case 1:
                    this.open = true;
                    break;
                case 2:
                    this.open = true;
                    await this.getCountries();
                    await this.getDocuments();
                    this.setShowStep1(false);
                    this.setShowSignUp(true);

                    if (storageData.data?.account) {
                        this.account = storageData.data.account;
                        this.selectedCountry = this.account.country ? this.account.country : '0';
                    }

                    break;
                case 3:
                    this.open = true;
                    this.setShowStep1(false);
                    this.setShowLinkAccount(true);

                    if (storageData.data?.linkAccount) {
                        this.linkAccount = storageData.data.linkAccount;
                        this.isLinkAccountBtnDisabled =
                            !this.linkAccount.unityAccountEmail || !this.linkAccount.dateBirth;
                    }

                    break;
            }
        }
    };

    removeFromSessionStorage = () => {
        window.sessionStorage.removeItem('linkAccountDialog');
    };

    @action
    clearAllErrors = () => {
        this.isPhoneInvalid = false;
        this.isEmailInvalid = false;
        this.isLinkUnityEmailInvalid = false;

        this.isDateOfBirthInvalid = false;
        this.isFirstNameInvalid = false;
        this.isLastNameInvalid = false;
        this.isDateOfBirthLinkInvalid = false;
        this.isLinkUnityAuthError = false;
    };

    @action
    activateResend = () => {
        this.setShowActivate(false);
        this.setShowStep3(true);
        this.sendVerificationCode();
    };

    @action
    setLastError = (err: { description: string; code?: string }) => {
        this.lastError = err;
    };

    @action
    clearLastError = () => {
        this.lastError = { description: '', code: undefined };
    };
}
